import React, { useRef } from 'react';
import BackendService from '../../services/admin/BackendService';
import type FundOpportunity from '../../models/FundOpportunity';
import type { AdminFundUpdateEntry } from '../../models/FundUpdateEntry';
import UploadOnlyDocumentPicker from '../admin/newContentManagement/UploadOnlyDocumentPicker';
import StyledModal from '../styles/StyledModal';
import { useUploadDocumentDraft } from './useUploadDocumentDraft';

interface UploadDocumentDialogProps {
  open: boolean;
  fundOpportunity: FundOpportunity;
  /** Creates the draft after the document import succeeds. */
  createDraft: () => Promise<AdminFundUpdateEntry | null>;
  onClose: () => void;
  /** A draft now exists and has the imported content saved into it — open it. */
  onImported: (draftId: string) => void;
}

/**
 * "Upload Document" from New Updates: for adding one new document and importing from it right
 * away — browsing/reusing documents already in the library is what the Docs tab (on an open
 * draft) is for, so this only shows the upload drop zone and the page/range/region picker for
 * whatever was just uploaded. The draft is created after a successful AI import.
 */
export default function UploadDocumentDialog({
  open,
  fundOpportunity,
  createDraft,
  onClose,
  onImported,
}: UploadDocumentDialogProps) {
  const backendService = useRef(new BackendService()).current;
  const draft = useUploadDocumentDraft({
    open,
    fundOpportunity,
    backendService,
    createDraft,
    onClose,
    onImported,
  });

  return (
    <StyledModal
      open={open}
      onClose={draft.finish}
      title="Upload Document"
      align="left"
      maxWidth="sm"
      fullWidth
    >
      <UploadOnlyDocumentPicker
        fundOpportunityId={fundOpportunity.id}
        backendService={backendService}
        modelType="FundUpdate"
        modelId=""
        onImported={draft.importSections}
        onBatchImported={draft.recordBatch}
        onCancel={draft.finish}
      />
    </StyledModal>
  );
}
